import { useState, useRef, useEffect } from 'react';
import {
  PlusIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  PresentationChartBarIcon,
  LinkIcon,
  ArrowTopRightOnSquareIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import { useTrainingUser } from './TrainingContext';
import { StandardHeader } from '../../components/common/StandardHeader';
import { MOCK_TRAINING_USERS } from './data/mockData';

// ── Mock data ────────────────────────────────────────────────
const MODULES_INIT = [
  {
    id: 'r1',
    title: 'Q1 Sales Onboarding Deck',
    description: 'Core sales process, objection handling, and ICP overview for new team members.',
    type: 'slides',
    url: 'https://docs.google.com/presentation/d/1HnZfD6lVSLEJrvaqT6P9-9QayJMgd6vfvQVb_QivcUM/edit?usp=sharing',
    embedUrl: 'https://docs.google.com/presentation/d/1HnZfD6lVSLEJrvaqT6P9-9QayJMgd6vfvQVb_QivcUM/preview',
    tags: ['Sales', 'Onboarding'],
    author: 'Sarah Johnson',
    addedAt: 'Mar 10, 2025',
    notes: 'Pay close attention to slides 4–7 on discovery questions.',
    quiz: [
      { q: 'What is the first step in the BANT qualification framework?', opts: ['Budget', 'Authority', 'Need', 'Timeline'], ans: 0, exp: 'Budget is the first factor — understanding if the prospect has funds allocated.' },
      { q: 'How many touchpoints does it take on average to convert a lead?', opts: ['3–5', '6–8', '8–12', '12+'], ans: 2, exp: 'Research shows 8–12 touchpoints are typically needed to convert a qualified prospect.' },
      { q: 'Which metric best indicates a healthy pipeline?', opts: ['Number of leads', 'Pipeline coverage ratio', 'Demo rate', 'Proposal volume'], ans: 1, exp: 'Pipeline coverage ratio (pipeline value vs. quota) is the most reliable health indicator.' },
      { q: 'What should you do immediately after a discovery call?', opts: ['Send a proposal', 'Send a follow-up summary', 'Schedule a demo', 'Check references'], ans: 1, exp: 'A follow-up summary reinforces key points and demonstrates professionalism.' },
    ],
    quizPublished: true,
  },
  {
    id: 'r2',
    title: 'Cold Outreach Playbook',
    description: 'Step-by-step guide for cold email and LinkedIn sequencing.',
    type: 'doc',
    url: 'https://docs.google.com/presentation/d/1MV_o28NTGRjNkh-V_IKd4569M2IpkT0JMjAyu2AqQwg/edit?usp=sharing',
    embedUrl: 'https://docs.google.com/presentation/d/1MV_o28NTGRjNkh-V_IKd4569M2IpkT0JMjAyu2AqQwg/preview',
    tags: ['Cold Outreach', 'Email'],
    author: 'Marcus Rivera',
    addedAt: 'Mar 15, 2025',
    notes: '',
    quiz: [
      { q: 'What is the ideal timeframe for the first client check-in after onboarding?', opts: ['24 hours', '48 hours', '1 week', '2 weeks'], ans: 1, exp: '48 hours gives clients time to explore but catches early issues before they escalate.' },
      { q: 'Which document should be shared at the kickoff meeting?', opts: ['Contract', 'Success plan', 'Invoice', 'Case studies'], ans: 1, exp: 'The success plan aligns expectations and gives the client a clear roadmap.' },
    ],
    quizPublished: false,
  },
  {
    id: 'r3',
    title: 'Product Demo Script',
    description: 'Approved talk track for live product demos. Updated Q1 2025.',
    type: 'slides',
    url: 'https://docs.google.com/presentation/d/1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms/edit',
    embedUrl: 'https://docs.google.com/presentation/d/1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms/embed?start=false&loop=false',
    tags: ['Demo', 'Sales'],
    author: 'Emily Chen',
    addedAt: 'Feb 28, 2025',
    notes: 'The pricing slide has been updated — make sure you use the new version.',
    quiz: [
      { q: 'What should you always personalize in a product demo?', opts: ['The logo', 'The use case examples', 'The pricing page', 'The loading screen'], ans: 1, exp: 'Tailoring use case examples to the prospect\'s industry dramatically improves engagement.' },
    ],
    quizPublished: true,
  },
];

// ── Helpers ───────────────────────────────────────────────────
function getEmbedUrl(url) {
  const slidesMatch = url.match(/docs\.google\.com\/presentation\/d\/([a-zA-Z0-9_-]+)/);
  if (slidesMatch) return `https://docs.google.com/presentation/d/${slidesMatch[1]}/embed?start=false&loop=false`;
  const docsMatch = url.match(/docs\.google\.com\/document\/d\/([a-zA-Z0-9_-]+)/);
  if (docsMatch) return `https://docs.google.com/document/d/${docsMatch[1]}/preview`;
  const sheetsMatch = url.match(/docs\.google\.com\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/);
  if (sheetsMatch) return `https://docs.google.com/spreadsheets/d/${sheetsMatch[1]}/preview`;
  return url;
}

function getResourceType(url) {
  if (url.includes('docs.google.com/presentation')) return 'slides';
  if (url.includes('docs.google.com/document'))     return 'doc';
  if (url.includes('docs.google.com/spreadsheets')) return 'sheet';
  if (url.toLowerCase().endsWith('.pdf'))            return 'pdf';
  return 'link';
}

const TYPE_META = {
  slides: { label: 'Slides', icon: PresentationChartBarIcon, color: 'bg-brand-50 border-brand-200 text-brand-600'         },
  doc:    { label: 'Doc',    icon: DocumentTextIcon,          color: 'bg-brightblue-50 border-brightblue-200 text-brightblue-600' },
  pdf:    { label: 'PDF',    icon: DocumentDuplicateIcon,     color: 'bg-teal-50 border-teal-200 text-teal-700'              },
  sheet:  { label: 'Sheet',  icon: DocumentDuplicateIcon,     color: 'bg-teal-50 border-teal-200 text-teal-700'              },
  link:   { label: 'Link',   icon: LinkIcon,                  color: 'bg-grey-50 border-grey-200 text-grey-500'              },
};

function TypeBadge({ type }) {
  const meta = TYPE_META[type] ?? TYPE_META.link;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.color}`}>
      {meta.label}
    </span>
  );
}

// ── User Switcher ─────────────────────────────────────────────
function UserSwitcher() {
  const { trainingUser, setTrainingUser } = useTrainingUser();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const initials = trainingUser?.name
    ? trainingUser.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-grey-100 transition-colors focus:outline-none"
      >
        <div className="w-7 h-7 rounded-full bg-darkblue-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
          {initials}
        </div>
        <span className="hidden sm:block text-sm font-medium text-darkblue-500 max-w-[110px] truncate">
          {trainingUser?.name?.split(' ')[0] ?? 'User'}
        </span>
        <span className={`hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
          trainingUser?.role === 'admin' ? 'bg-brand-50 text-brand-600' : 'bg-grey-100 text-grey-500'
        }`}>
          {trainingUser?.role ?? 'user'}
        </span>
        <ChevronDownIcon className={`w-3.5 h-3.5 text-grey-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[98]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-grey-200 rounded-xl shadow-lg z-[99] overflow-hidden">
            <div className="px-3 py-2 border-b border-grey-100 bg-grey-50">
              <p className="text-[10px] font-bold uppercase tracking-widest text-grey-400">Switch User</p>
            </div>
            <div className="py-1">
              {MOCK_TRAINING_USERS.map((u) => (
                <button
                  key={u.id}
                  onClick={() => { setTrainingUser(u); setOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-grey-50 ${trainingUser?.id === u.id ? 'bg-brightblue-50' : ''}`}
                >
                  <div className="w-7 h-7 rounded-full bg-darkblue-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                    {u.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-darkblue-500 truncate">{u.name}</p>
                    <p className="text-[10px] text-grey-400 truncate">{u.email}</p>
                  </div>
                  <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                    u.role === 'admin' ? 'bg-brand-50 text-brand-600' : 'bg-grey-100 text-grey-500'
                  }`}>
                    {u.role}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Resource Modal (add & edit) ───────────────────────────────
// Pass `existing` to open in edit mode — form pre-populates and
// onSave receives the updated resource with the same id/author/addedAt/notes.
function ModuleModal({ onClose, onSave, currentUser, existing = null }) {
  const isEdit = !!existing;

  const [form, setForm] = useState({
    title:       existing?.title       ?? '',
    description: existing?.description ?? '',
    url:         existing?.url         ?? '',
    tags:        existing?.tags?.join(', ') ?? '',
  });
  const [preview, setPreview] = useState(existing?.type ?? null);

  const handleUrlBlur = () => {
    if (!form.url.trim()) return;
    const type = getResourceType(form.url);
    setPreview(type);
    if (!form.title) {
      const labels = { slides: 'New Slides', doc: 'New Document', pdf: 'New PDF', link: 'New Module' };
      setForm((f) => ({ ...f, title: labels[type] ?? 'New Module' }));
    }
  };

  const handleSave = () => {
    if (!form.title.trim() || !form.url.trim()) return;
    const type = getResourceType(form.url);
    onSave({
      // Preserve existing fields when editing, generate new ones when adding
      id:        existing?.id       ?? `r${Date.now()}`,
      author:    existing?.author   ?? currentUser.name,
      addedAt:   existing?.addedAt  ?? new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      notes:     existing?.notes    ?? '',
      // Always recalculate from form
      title:       form.title,
      description: form.description,
      type,
      url:      form.url,
      embedUrl: getEmbedUrl(form.url),
      tags:     form.tags.split(',').map((t) => t.trim()).filter(Boolean),
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-grey-200 bg-grey-50 rounded-t-xl">
          <h3 className="text-base font-semibold text-darkblue-500">{isEdit ? 'Edit Module' : 'Add Module'}</h3>
          <button onClick={onClose} className="p-1.5 text-grey-400 hover:bg-grey-100 rounded-lg transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* URL first — auto-detects type */}
          <div>
            <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">
              Link <span className="text-pink-500">*</span>
            </label>
            <input
              value={form.url}
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
              onBlur={handleUrlBlur}
              placeholder="Paste a Google Slides, Doc, PDF, or any URL…"
              className="w-full px-3 py-2 border border-grey-300 rounded-lg text-sm text-darkblue-500 bg-white outline-none focus:ring-2 focus:ring-brightblue-300 focus:border-brightblue-400 transition-colors"
            />
            {preview && (
              <div className="mt-1.5 flex items-center gap-1.5">
                <TypeBadge type={preview} />
                <span className="text-xs text-grey-400">type detected</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">
              Title <span className="text-pink-500">*</span>
            </label>
            <input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Q1 Sales Onboarding Deck"
              className="w-full px-3 py-2 border border-grey-300 rounded-lg text-sm text-darkblue-500 bg-white outline-none focus:ring-2 focus:ring-brightblue-300 focus:border-brightblue-400 transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Brief description of this module…"
              rows={2}
              className="w-full px-3 py-2 border border-grey-300 rounded-lg text-sm text-darkblue-500 bg-white outline-none focus:ring-2 focus:ring-brightblue-300 focus:border-brightblue-400 transition-colors resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">
              Tags <span className="text-grey-400 font-normal">(comma-separated)</span>
            </label>
            <input
              value={form.tags}
              onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
              placeholder="e.g. Sales, Onboarding, Demo"
              className="w-full px-3 py-2 border border-grey-300 rounded-lg text-sm text-darkblue-500 bg-white outline-none focus:ring-2 focus:ring-brightblue-300 focus:border-brightblue-400 transition-colors"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-grey-200 bg-grey-50 rounded-b-xl flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-grey-300 text-grey-600 rounded-lg text-sm font-medium hover:bg-grey-100 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!form.title.trim() || !form.url.trim()}
            className="px-4 py-2 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors disabled:opacity-40"
          >
            {isEdit ? 'Save Changes' : 'Add Module'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Quiz Editor Modal (admin) ────────────────────────────────
function QuizEditorModal({ quiz = [], onClose, onSave }) {
  const [questions, setQuestions] = useState(
    quiz.map((q) => ({ ...q, opts: [...q.opts] }))
  );

  const addQuestion = () =>
    setQuestions((p) => [...p, { q: '', opts: ['', '', '', ''], ans: 0, exp: '' }]);

  const updateQuestion = (i, field, value) =>
    setQuestions((p) => p.map((q, idx) => (idx === i ? { ...q, [field]: value } : q)));

  const updateOption = (qi, oi, value) =>
    setQuestions((p) =>
      p.map((q, idx) =>
        idx === qi ? { ...q, opts: q.opts.map((o, j) => (j === oi ? value : o)) } : q
      )
    );

  const removeQuestion = (i) => setQuestions((p) => p.filter((_, idx) => idx !== i));

  const valid = questions.length > 0 && questions.every((q) => q.q.trim() && q.opts.every((o) => o.trim()));

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-grey-200 bg-grey-50 rounded-t-xl">
          <h3 className="text-base font-semibold text-darkblue-500">Edit Quiz</h3>
          <button onClick={onClose} className="p-1.5 text-grey-400 hover:bg-grey-100 rounded-lg transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {questions.map((question, qi) => (
            <div key={qi} className="border border-grey-200 rounded-lg p-4 space-y-3 bg-grey-50/50">
              <div className="flex items-start justify-between gap-2">
                <span className="text-xs font-bold text-grey-400 mt-1">Q{qi + 1}</span>
                <button onClick={() => removeQuestion(qi)} className="p-1 text-grey-400 hover:text-red-500 transition-colors">
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
              <input
                value={question.q}
                onChange={(e) => updateQuestion(qi, 'q', e.target.value)}
                placeholder="Question text…"
                className="w-full px-3 py-2 border border-grey-300 rounded-lg text-sm text-darkblue-500 bg-white outline-none focus:ring-2 focus:ring-brightblue-300"
              />
              <div className="space-y-2">
                {question.opts.map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <button
                      onClick={() => updateQuestion(qi, 'ans', oi)}
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        question.ans === oi ? 'border-teal-500 bg-teal-500' : 'border-grey-300 hover:border-grey-400'
                      }`}
                    >
                      {question.ans === oi && <div className="w-2 h-2 rounded-full bg-white" />}
                    </button>
                    <input
                      value={opt}
                      onChange={(e) => updateOption(qi, oi, e.target.value)}
                      placeholder={`Option ${oi + 1}`}
                      className="flex-1 px-2.5 py-1.5 border border-grey-300 rounded-lg text-xs text-darkblue-500 bg-white outline-none focus:ring-2 focus:ring-brightblue-300"
                    />
                  </div>
                ))}
              </div>
              <input
                value={question.exp}
                onChange={(e) => updateQuestion(qi, 'exp', e.target.value)}
                placeholder="Explanation (shown after answering)…"
                className="w-full px-3 py-1.5 border border-grey-200 rounded-lg text-xs text-grey-500 bg-white outline-none focus:ring-2 focus:ring-brightblue-300"
              />
            </div>
          ))}
          <button
            onClick={addQuestion}
            className="w-full py-2 border-2 border-dashed border-grey-300 rounded-lg text-xs font-medium text-grey-500 hover:border-brand-400 hover:text-brand-600 transition-colors"
          >
            + Add Question
          </button>
        </div>

        <div className="px-6 py-4 border-t border-grey-200 bg-grey-50 rounded-b-xl flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-grey-300 text-grey-600 rounded-lg text-sm font-medium hover:bg-grey-100 transition-colors">
            Cancel
          </button>
          <button
            onClick={() => { onSave(questions); onClose(); }}
            disabled={!valid}
            className="px-4 py-2 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors disabled:opacity-40"
          >
            Save Quiz
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Quiz Full View (replaces iframe in main panel) ──────────
function QuizFullView({ resource, userId, quizResults, onQuizSubmit, onExitQuiz }) {
  const quiz = resource.quiz || [];
  const userResult = quizResults?.[userId]?.[resource.id];

  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);

  const handleSubmit = () => {
    let correct = 0;
    quiz.forEach((q, i) => { if (answers[i] === q.ans) correct++; });
    const result = { score: correct, total: quiz.length, answers: { ...answers }, completedAt: new Date().toISOString() };
    setScore(result);
    setSubmitted(true);
    onQuizSubmit(resource.id, result);
  };

  const allAnswered = quiz.length > 0 && Object.keys(answers).length === quiz.length;
  const pct = score ? Math.round((score.score / score.total) * 100) : 0;

  // ── Results view ──
  if (submitted && score) {
    return (
      <div className="h-full overflow-y-auto">
        {/* Score header */}
        <div className={`px-8 py-8 text-center ${pct >= 70 ? 'bg-teal-50' : 'bg-yellow-50'}`}>
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${pct >= 70 ? 'bg-teal-100' : 'bg-yellow-100'}`}>
            {pct >= 70
              ? <CheckCircleSolid className="w-8 h-8 text-teal-600" />
              : <XCircleIcon className="w-8 h-8 text-yellow-600" />}
          </div>
          <h2 className="text-2xl font-bold text-darkblue-500">{score.score} / {score.total}</h2>
          <p className={`text-sm font-medium mt-1 ${pct >= 70 ? 'text-teal-600' : 'text-yellow-600'}`}>
            {pct}% — {pct >= 70 ? 'Great job!' : 'Keep studying!'}
          </p>
          <div className="w-48 h-2.5 bg-grey-200 rounded-full overflow-hidden mx-auto mt-4">
            <div
              className={`h-full rounded-full transition-all ${pct >= 70 ? 'bg-teal-500' : 'bg-yellow-500'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex gap-3 justify-center mt-6">
            <button
              onClick={() => { setAnswers({}); setSubmitted(false); setScore(null); setCurrentQ(0); }}
              className="px-4 py-2 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
            >
              Retake Quiz
            </button>
            <button
              onClick={onExitQuiz}
              className="px-4 py-2 border border-grey-300 text-grey-600 rounded-lg text-sm font-medium hover:bg-grey-100 transition-colors"
            >
              Back to Deck
            </button>
          </div>
        </div>

        {/* Per-question breakdown */}
        <div className="px-8 py-6 space-y-4 max-w-2xl mx-auto">
          {quiz.map((q, i) => {
            const correct = answers[i] === q.ans;
            return (
              <div key={i} className={`border rounded-xl p-5 ${correct ? 'border-teal-200 bg-teal-50/40' : 'border-red-200 bg-red-50/40'}`}>
                <div className="flex items-start gap-3 mb-3">
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${correct ? 'bg-teal-500' : 'bg-red-400'}`}>
                    {i + 1}
                  </span>
                  <p className="text-sm font-semibold text-darkblue-500">{q.q}</p>
                </div>
                <div className="space-y-2 ml-9">
                  {q.opts.map((opt, oi) => {
                    const isCorrect = oi === q.ans;
                    const isChosen = oi === answers[i];
                    let cls = 'border-grey-200 text-grey-500';
                    if (isCorrect) cls = 'border-teal-300 bg-teal-50 text-teal-700 font-medium';
                    else if (isChosen) cls = 'border-red-300 bg-red-50 text-red-600 line-through';
                    return (
                      <div key={oi} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${cls}`}>
                        {isCorrect ? <CheckCircleSolid className="w-4 h-4 text-teal-500 flex-shrink-0" /> :
                         isChosen ? <XCircleIcon className="w-4 h-4 text-red-400 flex-shrink-0" /> :
                         <span className="w-4 h-4 flex-shrink-0" />}
                        {opt}
                      </div>
                    );
                  })}
                </div>
                {q.exp && (
                  <p className="ml-9 mt-3 text-xs text-grey-500 italic border-l-2 border-grey-200 pl-3">{q.exp}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Quiz taking view (one question at a time) ──
  const q = quiz[currentQ];
  return (
    <div className="h-full flex flex-col bg-grey-50">
      {/* Progress bar & header */}
      <div className="flex-shrink-0 bg-white border-b border-grey-200 px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <AcademicCapIcon className="w-5 h-5 text-brand-500" />
            <span className="text-sm font-semibold text-darkblue-500">Quiz: {resource.title}</span>
          </div>
          <button onClick={onExitQuiz} className="text-xs text-grey-500 hover:text-darkblue-500 transition-colors">
            Exit Quiz
          </button>
        </div>
        {/* Progress dots */}
        <div className="flex items-center gap-1.5">
          {quiz.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentQ(i)}
              className={`h-2 rounded-full transition-all ${
                i === currentQ ? 'w-6 bg-brand-500' :
                answers[i] !== undefined ? 'w-2 bg-teal-400' : 'w-2 bg-grey-300'
              }`}
            />
          ))}
          <span className="ml-2 text-xs text-grey-400">{Object.keys(answers).length}/{quiz.length} answered</span>
        </div>
      </div>

      {/* Question card */}
      <div className="flex-1 flex items-center justify-center p-6 overflow-y-auto">
        <div className="w-full max-w-xl">
          <p className="text-xs font-bold text-brand-500 uppercase tracking-widest mb-2">Question {currentQ + 1} of {quiz.length}</p>
          <h3 className="text-lg font-semibold text-darkblue-500 mb-6">{q.q}</h3>
          <div className="space-y-3">
            {q.opts.map((opt, oi) => (
              <button
                key={oi}
                onClick={() => setAnswers((a) => ({ ...a, [currentQ]: oi }))}
                className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all text-sm ${
                  answers[currentQ] === oi
                    ? 'border-brand-500 bg-brand-50 text-brand-700 font-medium shadow-sm'
                    : 'border-grey-200 text-grey-700 hover:border-grey-300 hover:bg-white'
                }`}
              >
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold mr-3 ${
                  answers[currentQ] === oi ? 'bg-brand-500 text-white' : 'bg-grey-200 text-grey-500'
                }`}>
                  {String.fromCharCode(65 + oi)}
                </span>
                {opt}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation footer */}
      <div className="flex-shrink-0 bg-white border-t border-grey-200 px-6 py-4 flex items-center justify-between">
        <button
          onClick={() => setCurrentQ((c) => Math.max(0, c - 1))}
          disabled={currentQ === 0}
          className="px-4 py-2 border border-grey-300 text-grey-600 rounded-lg text-sm font-medium hover:bg-grey-100 transition-colors disabled:opacity-30"
        >
          Previous
        </button>
        <div className="flex gap-3">
          {currentQ < quiz.length - 1 ? (
            <button
              onClick={() => setCurrentQ((c) => Math.min(quiz.length - 1, c + 1))}
              className="px-4 py-2 bg-darkblue-500 text-white rounded-lg text-sm font-medium hover:bg-darkblue-600 transition-colors"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!allAnswered}
              className="px-6 py-2 bg-brand-500 text-white rounded-lg text-sm font-semibold hover:bg-brand-600 transition-colors disabled:opacity-40"
            >
              Submit Quiz
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Quiz Tab Content (summary in bottom panel) ──────────────
function QuizTabContent({ resource, isAdmin, userId, quizResults, onQuizEdit, onTogglePublish, onStartQuiz }) {
  const quiz = resource.quiz || [];
  const published = resource.quizPublished ?? false;
  const userResult = quizResults?.[userId]?.[resource.id];

  // ── Admin view ──
  if (isAdmin) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AcademicCapIcon className="w-4 h-4 text-darkblue-500" />
            <span className="text-xs font-semibold text-darkblue-500">
              Quiz ({quiz.length} question{quiz.length !== 1 ? 's' : ''})
            </span>
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
              published ? 'bg-teal-50 text-teal-700 border border-teal-200' : 'bg-grey-100 text-grey-500 border border-grey-200'
            }`}>
              {published ? 'Published' : 'Draft'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onTogglePublish(resource.id)}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors ${
                published
                  ? 'bg-grey-100 text-grey-600 hover:bg-grey-200'
                  : 'bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200'
              }`}
            >
              {published ? 'Unpublish' : 'Publish'}
            </button>
            <button
              onClick={() => onQuizEdit(resource)}
              className="px-2.5 py-1 bg-darkblue-500 text-white rounded-lg text-[11px] font-medium hover:bg-darkblue-600 transition-colors"
            >
              Edit Quiz
            </button>
          </div>
        </div>
        {quiz.length === 0 ? (
          <p className="text-xs text-grey-400">No questions yet. Click "Edit Quiz" to add questions.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {quiz.map((q, i) => (
              <span key={i} className="text-[10px] bg-grey-100 text-grey-500 px-2 py-1 rounded-lg truncate max-w-[200px]">
                Q{i + 1}: {q.q}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Learner: no quiz / not published ──
  if (!published || quiz.length === 0) {
    return (
      <div className="flex items-center gap-3 text-grey-400">
        <AcademicCapIcon className="w-5 h-5 text-grey-300" />
        <p className="text-xs font-medium">No quiz available for this module yet.</p>
      </div>
    );
  }

  // ── Learner: completed ──
  if (userResult) {
    const pct = Math.round((userResult.score / userResult.total) * 100);
    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CheckCircleSolid className={`w-5 h-5 ${pct >= 70 ? 'text-teal-500' : 'text-yellow-500'}`} />
          <div>
            <p className="text-xs font-semibold text-darkblue-500">
              Quiz Completed — {userResult.score}/{userResult.total} ({pct}%)
            </p>
            <div className="w-32 h-1.5 bg-grey-200 rounded-full overflow-hidden mt-1">
              <div className={`h-full rounded-full ${pct >= 70 ? 'bg-teal-500' : 'bg-yellow-500'}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onStartQuiz}
            className="px-3 py-1.5 bg-brand-500 text-white rounded-lg text-xs font-medium hover:bg-brand-600 transition-colors"
          >
            Retake Quiz
          </button>
        </div>
      </div>
    );
  }

  // ── Learner: not started ──
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <AcademicCapIcon className="w-5 h-5 text-brand-500" />
        <div>
          <p className="text-xs font-semibold text-darkblue-500">Quiz Available</p>
          <p className="text-[10px] text-grey-400">{quiz.length} question{quiz.length !== 1 ? 's' : ''}</p>
        </div>
      </div>
      <button
        onClick={onStartQuiz}
        className="px-4 py-2 bg-brand-500 text-white rounded-lg text-xs font-semibold hover:bg-brand-600 transition-colors"
      >
        Start Quiz
      </button>
    </div>
  );
}

// ── Resource Viewer (right panel) ─────────────────────────────
function ModuleViewer({ resource, resources, onSelect, onNotesChange, isAdmin, userId, quizResults, onQuizSubmit, onQuizEdit, onTogglePublish }) {
  const [activeTab, setActiveTab]     = useState('notes');
  const [tabsExpanded, setTabsExpanded] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [quizMode, setQuizMode]         = useState(false);
  const iframeRef = useRef(null);

  // Exit quiz mode when resource changes
  useEffect(() => { setQuizMode(false); }, [resource.id]);

  const related = resources.filter(
    (r) => r.id !== resource.id && r.tags.some((t) => resource.tags.includes(t))
  );

  const quizCount = resource.quiz?.length || 0;
  const userResult = quizResults?.[userId]?.[resource.id];

  const TABS = [
    { id: 'notes',   label: 'Notes'   },
    { id: 'details', label: 'Details' },
    { id: 'related', label: `Related${related.length ? ` (${related.length})` : ''}` },
    { id: 'quiz',    label: (
      <span className="flex items-center gap-1">
        Quiz{quizCount ? ` (${quizCount})` : ''}
        {userResult && !isAdmin && <CheckCircleSolid className="w-3 h-3 text-teal-500" />}
      </span>
    )},
  ];

  const viewerClasses = isFullScreen
    ? 'fixed inset-0 z-50 bg-grey-50 flex flex-col'
    : 'flex flex-col h-full';

  return (
    <div className={viewerClasses}>
      {/* ── Viewer toolbar ── */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-grey-200 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <TypeBadge type={resource.type} />
          <span className="text-sm font-semibold text-darkblue-500 truncate">{resource.title}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <a
            href={resource.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 border border-grey-200 text-grey-600 rounded-lg text-xs font-medium hover:bg-grey-50 transition-colors"
          >
            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" /> Open
          </a>
          <button
            onClick={() => setIsFullScreen((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-grey-200 text-grey-600 rounded-lg text-xs font-medium hover:bg-grey-50 transition-colors"
          >
            {isFullScreen
              ? <><ArrowsPointingInIcon className="w-3.5 h-3.5" /> Exit Full Screen</>
              : <><ArrowsPointingOutIcon className="w-3.5 h-3.5" /> Full Screen</>}
          </button>
        </div>
      </div>

      {/* ── Main content area (iframe or quiz) ── */}
      <div className="flex-1 bg-grey-100 min-h-0">
        {quizMode ? (
          <QuizFullView
            resource={resource}
            userId={userId}
            quizResults={quizResults}
            onQuizSubmit={onQuizSubmit}
            onExitQuiz={() => setQuizMode(false)}
          />
        ) : (
          <iframe
            ref={iframeRef}
            src={resource.embedUrl}
            title={resource.title}
            className="w-full h-full border-0"
            allowFullScreen
          />
        )}
      </div>

      {/* ── Bottom tabs ── */}
      <div className="flex-shrink-0 bg-white border-t border-grey-200">
        {/* Tab bar */}
        <div className="flex items-center border-b border-grey-100">
          <div className="flex flex-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (!tabsExpanded) setTabsExpanded(true);
                }}
                className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors ${
                  activeTab === tab.id && tabsExpanded
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-grey-500 hover:text-darkblue-500'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* Expand / collapse toggle */}
          <button
            onClick={() => setTabsExpanded((v) => !v)}
            className="px-3 py-2.5 text-grey-400 hover:text-darkblue-500 transition-colors"
            title={tabsExpanded ? 'Collapse' : 'Expand'}
          >
            <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${tabsExpanded ? '' : 'rotate-180'}`} />
          </button>
        </div>

        {/* Tab content */}
        {tabsExpanded && (
          <div className="h-40 overflow-y-auto p-4">
            {/* Notes */}
            {activeTab === 'notes' && (
              <textarea
                value={resource.notes}
                onChange={(e) => onNotesChange(resource.id, e.target.value)}
                placeholder="Add your notes about this module…"
                className="w-full h-full resize-none text-sm text-darkblue-500 placeholder:text-grey-300 outline-none bg-transparent"
              />
            )}

            {/* Details */}
            {activeTab === 'details' && (
              <div className="space-y-2 text-xs">
                {[
                  { label: 'Author',   value: resource.author  },
                  { label: 'Added',    value: resource.addedAt },
                  { label: 'Type',     value: TYPE_META[resource.type]?.label ?? resource.type },
                  { label: 'Tags',     value: resource.tags.join(', ') || '—' },
                  { label: 'Source',   value: (
                    <a href={resource.url} target="_blank" rel="noopener noreferrer"
                       className="text-brightblue-600 hover:underline truncate block max-w-xs">
                      {resource.url}
                    </a>
                  )},
                ].map(({ label, value }) => (
                  <div key={label} className="flex gap-3">
                    <span className="font-semibold text-darkblue-500 w-14 flex-shrink-0">{label}</span>
                    <span className="text-grey-500">{value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Related */}
            {activeTab === 'related' && (
              related.length === 0 ? (
                <p className="text-xs text-grey-400">No related modules found.</p>
              ) : (
                <div className="space-y-2">
                  {related.map((r) => {
                    const Icon = TYPE_META[r.type]?.icon ?? LinkIcon;
                    return (
                      <button
                        key={r.id}
                        onClick={() => onSelect(r)}
                        className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-grey-50 transition-colors text-left"
                      >
                        <div className="w-8 h-8 rounded-lg bg-darkblue-500 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-darkblue-500 truncate">{r.title}</p>
                          <p className="text-[10px] text-grey-400">{r.tags.join(' · ')}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )
            )}

            {/* Quiz */}
            {activeTab === 'quiz' && (
              <QuizTabContent
                resource={resource}
                isAdmin={isAdmin}
                userId={userId}
                quizResults={quizResults}
                onQuizEdit={onQuizEdit}
                onTogglePublish={onTogglePublish}
                onStartQuiz={() => setQuizMode(true)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Resource List Item ────────────────────────────────────────
function ModuleListItem({ resource, isSelected, isAdmin, onClick, onEdit }) {
  const Icon = TYPE_META[resource.type]?.icon ?? LinkIcon;
  return (
    <div
      className={`w-full flex items-start gap-3 p-3 rounded-xl transition-colors ${
        isSelected
          ? 'bg-brightblue-50 border border-brightblue-200'
          : 'bg-white border border-grey-200 hover:shadow-sm hover:border-grey-300'
      }`}
    >
      {/* Clickable area */}
      <button onClick={onClick} className="flex items-start gap-3 flex-1 min-w-0 text-left">
        <div className="w-9 h-9 rounded-lg bg-darkblue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Icon className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-darkblue-500 truncate">{resource.title}</p>
          <p className="text-[10px] text-grey-400 mt-0.5 truncate">{resource.description}</p>
          <div className="flex flex-wrap gap-1 mt-1.5">
            {resource.tags.map((t) => (
              <span key={t} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-brand-50 text-brand-600 border border-brand-200">
                {t}
              </span>
            ))}
          </div>
        </div>
      </button>

      {/* Edit button — admin only */}
      {isAdmin && (
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(resource); }}
          className="flex-shrink-0 p-1.5 text-grey-400 hover:text-darkblue-500 hover:bg-grey-100 rounded-lg transition-colors mt-0.5"
          title="Edit module"
        >
          <PencilIcon className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────
export default function Modules() {
  const { trainingUser } = useTrainingUser();
  const isAdmin = trainingUser.role === 'admin';

  const [resources, setResources]         = useState(MODULES_INIT);
  const [selected, setSelected]           = useState(MODULES_INIT[0]);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [showAddModal, setShowAddModal]   = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [search, setSearch]               = useState('');
  const [quizEditing, setQuizEditing]     = useState(null); // resource being quiz-edited
  const [quizResults, setQuizResults]     = useState({}); // { [userId]: { [resourceId]: { score, total, answers, completedAt } } }

  const filtered = resources.filter((r) =>
    !search || r.title.toLowerCase().includes(search.toLowerCase()) || r.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()))
  );

  const handleNotesChange = (id, notes) =>
    setResources((p) => p.map((r) => r.id === id ? { ...r, notes } : r));

  const handleAdd = (resource) => {
    setResources((p) => [resource, ...p]);
    setSelected(resource);
  };

  const handleEdit = (updated) => {
    setResources((p) => p.map((r) => r.id === updated.id ? updated : r));
    if (selected?.id === updated.id) setSelected(updated);
  };

  const handleQuizSave = (questions) => {
    const update = (r) => r.id === quizEditing.id ? { ...r, quiz: questions } : r;
    setResources((p) => p.map(update));
    if (selected?.id === quizEditing.id) setSelected((s) => ({ ...s, quiz: questions }));
  };

  const handleTogglePublish = (resourceId) => {
    const update = (r) => r.id === resourceId ? { ...r, quizPublished: !r.quizPublished } : r;
    setResources((p) => p.map(update));
    if (selected?.id === resourceId) setSelected((s) => ({ ...s, quizPublished: !s.quizPublished }));
  };

  const handleQuizSubmit = (resourceId, result) => {
    setQuizResults((prev) => ({
      ...prev,
      [trainingUser.id]: {
        ...(prev[trainingUser.id] || {}),
        [resourceId]: result,
      },
    }));
  };

  return (
    <div className="min-h-screen bg-grey-50 flex flex-col">
      <div className="p-4 md:p-6 pb-0 flex-shrink-0">
        <StandardHeader
          title="Modules"
          subtitle="Training modules, slide decks, and quizzes"
          isRefreshing={false}
          customControls={
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-400 text-darkblue-500 border border-teal-300">
                {resources.length} module{resources.length !== 1 ? 's' : ''}
              </span>
              <UserSwitcher />
            </div>
          }
        />
      </div>

      {/* ── Split layout ── */}
      <div className="flex flex-1 gap-0 p-4 md:p-6 pt-4 min-h-0" style={{ height: 'calc(100vh - 140px)' }}>

        {/* ── Left panel — resource list ── */}
        <div className={`flex flex-col flex-shrink-0 transition-all duration-300 ${listCollapsed ? 'w-0 overflow-hidden' : 'w-72 mr-4'}`}>
          {/* List header */}
          <div className="flex items-center justify-between mb-3 flex-shrink-0">
            <p className="text-xs font-bold uppercase tracking-widest text-grey-400">
              {filtered.length} Module{filtered.length !== 1 ? 's' : ''}
            </p>
            {isAdmin && (
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-darkblue-500 text-white rounded-lg text-xs font-medium hover:bg-darkblue-600 transition-colors"
              >
                <PlusIcon className="w-3.5 h-3.5" /> Add
              </button>
            )}
          </div>

          {/* Search */}
          <div className="relative mb-3 flex-shrink-0">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search modules…"
              className="w-full pl-3 pr-3 py-2 border border-grey-300 rounded-lg text-xs text-darkblue-500 bg-white outline-none focus:ring-2 focus:ring-brightblue-300 focus:border-brightblue-400 transition-colors"
            />
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {filtered.map((r) => (
              <ModuleListItem
                key={r.id}
                resource={r}
                isSelected={selected?.id === r.id}
                isAdmin={isAdmin}
                onClick={() => setSelected(r)}
                onEdit={setEditingResource}
              />
            ))}
            {filtered.length === 0 && (
              <p className="text-xs text-grey-400 text-center pt-8">No modules match your search.</p>
            )}
          </div>
        </div>

        {/* ── Collapse toggle button ── */}
        <button
          onClick={() => setListCollapsed((v) => !v)}
          className="flex-shrink-0 self-center w-5 h-12 bg-white border border-grey-200 rounded-lg flex items-center justify-center text-grey-400 hover:text-darkblue-500 hover:border-grey-300 transition-colors mr-4 shadow-sm"
          title={listCollapsed ? 'Show list' : 'Hide list'}
        >
          {listCollapsed
            ? <ChevronRightIcon className="w-3 h-3" />
            : <ChevronLeftIcon className="w-3 h-3" />}
        </button>

        {/* ── Right panel — viewer ── */}
        <div className="flex-1 bg-white border border-grey-200 rounded-xl overflow-hidden min-w-0">
          {selected ? (
            <ModuleViewer
              resource={selected}
              resources={resources}
              onSelect={setSelected}
              onNotesChange={handleNotesChange}
              isAdmin={isAdmin}
              userId={trainingUser.id}
              quizResults={quizResults}
              onQuizSubmit={handleQuizSubmit}
              onQuizEdit={setQuizEditing}
              onTogglePublish={handleTogglePublish}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-grey-400">
              <PresentationChartBarIcon className="w-12 h-12 text-grey-300 mb-3" />
              <p className="font-semibold text-grey-600 mb-1">No module selected</p>
              <p className="text-sm">Choose a module from the list to view it here</p>
            </div>
          )}
        </div>
      </div>

      {showAddModal && (
        <ModuleModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAdd}
          currentUser={trainingUser}
        />
      )}
      {editingResource && (
        <ModuleModal
          existing={editingResource}
          onClose={() => setEditingResource(null)}
          onSave={handleEdit}
          currentUser={trainingUser}
        />
      )}
      {quizEditing && (
        <QuizEditorModal
          quiz={quizEditing.quiz || []}
          onClose={() => setQuizEditing(null)}
          onSave={handleQuizSave}
        />
      )}
    </div>
  );
}